import { useMemo } from 'react';
import useGoogleSheets from 'use-google-sheets';
import { Question } from '../types';
import { partitionValidQuestions } from '../utils/questionValidation';

interface UseQuestionsReturn {
  questions: Question[];
  easyQuestions: Question[];
  mediumQuestions: Question[];
  hardQuestions: Question[];
  actualQuestion: Question;
  loading: boolean;
  error: any;
}

interface UseQuestionsProps {
  questionId?: string;
}

const EMPTY_QUESTION: Question = {
  id: "",
  pregunta: "",
  "1": "",
  "2": "",
  "3": "",
  "4": "",
  respuesta: "",
  usada: "",
  dificultad: null,
};

export const useQuestions = ({ questionId }: UseQuestionsProps = {}): UseQuestionsReturn => {
  const dateData = useGoogleSheets({
    apiKey: process.env.REACT_APP_GOOGLE_API_KEY ?? "",
    sheetId: process.env.REACT_APP_GOOGLE_BOOK_SHEET_ID ?? "",
    sheetsOptions: [{ id: "Fecha" }],
  });

  const { data, loading, error } = useGoogleSheets({
    apiKey: process.env.REACT_APP_GOOGLE_API_KEY ?? "",
    sheetId: process.env.REACT_APP_GOOGLE_BOOK_SHEET_ID ?? "",
    sheetsOptions: [{ id: "Preguntas" }],
  });

  const date = useMemo(() => {
    if (dateData.data[0]?.data[0] && typeof dateData.data[0].data[0] === 'object' && 'fecha' in dateData.data[0].data[0]) {
      return { fecha: (dateData.data[0].data[0] as { fecha?: string }).fecha };
    }
    return undefined;
  }, [dateData.data]);

  // Fallback to localStorage if loading or no data
  const cachedQuestionsData = useMemo(() => {
    const stored = localStorage.getItem('questions');
    try {
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed.questions) ? parsed.questions : [];
      }
    } catch (e) {
      // Error ignored during cleanup
    }
    return [];
  }, []);

  const processedData = useMemo(() => {
    // If we have an error or no data at all (not even cache), return empty
    if (error || (!data && cachedQuestionsData.length === 0)) {
      return {
        questions: [],
        easyQuestions: [],
        mediumQuestions: [],
        hardQuestions: [],
        actualQuestion: EMPTY_QUESTION,
      };
    }

    // Use fetched data if available, otherwise fallback to cache
    const sheetRows = (data && data[0] && Array.isArray(data[0].data)) 
      ? (data[0].data as Question[]) 
      : cachedQuestionsData;
    const questions = sheetRows.filter((question: Question) => question && (question.pregunta !== null && question.pregunta !== undefined));

    let filtered_questions = questions.filter((question: Question) => 'pregunta' in question);
    let latest_update_str = localStorage.getItem('latest_update');
    const latest_update = latest_update_str ? JSON.parse(latest_update_str) : null;
    const currentDateStr = JSON.stringify(date);

    if (!latest_update || JSON.stringify(latest_update.date) !== currentDateStr) {
      localStorage.setItem('questions', JSON.stringify({
        questions: filtered_questions,
      }));

      localStorage.setItem('latest_update', JSON.stringify({
        date: date,
      }));
    }

    // Exclude rows with a missing option or a "respuesta" that doesn't match any
    // option exactly - they'd otherwise become a silently unwinnable question.
    const valid_questions = partitionValidQuestions(filtered_questions);

    const easyQuestions = valid_questions.filter((question: Question) => question.dificultad === '0');
    const mediumQuestions = valid_questions.filter((question: Question) => question.dificultad === '1');
    const hardQuestions = valid_questions.filter((question: Question) => question.dificultad === '2');
    const actualQuestion = questionId
      ? valid_questions.find((question: Question) => String(question.id) === String(questionId)) || EMPTY_QUESTION
      : EMPTY_QUESTION;

    // Question ID check


    // Removed status and phases initialization side effects to avoid racing with useGameLogic


    return {
      questions,
      easyQuestions,
      mediumQuestions,
      hardQuestions,
      actualQuestion,
    };
  }, [data, error, questionId, date, cachedQuestionsData]);

  return {
    ...processedData,
    loading,
    error,
  };
};